import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { handlePasswordReset } from '../lib/supabase';
import { Mail, AlertCircle, CheckCircle, ArrowLeft, Shield } from 'lucide-react';

const ResetPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setError('Por favor, introduce tu email');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Por favor, introduce un email válido');
      return;
    }

    try {
      setError('');
      setLoading(true);

      const result = await handlePasswordReset(email);
      
      if (!result.success) {
        throw new Error(result.message);
      }

      setSuccess(true);
    } catch (err: any) {
      console.error('Error al enviar email de recuperación:', err);
      setError(err.message || 'Error al enviar el email de recuperación');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-dark-card rounded-lg shadow-xl p-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-green-500/10 rounded-full p-3">
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>
          </div>

          <h2 className="text-2xl font-bold text-white mb-4">
            ¡Email Enviado!
          </h2>

          <p className="text-gray-300 mb-8">
            Te hemos enviado un email con instrucciones para restablecer tu contraseña. 
            Por favor, revisa tu bandeja de entrada y la carpeta de spam.
          </p>

          <div className="space-y-4">
            <div className="p-4 bg-dark-overlay rounded-lg text-sm text-gray-400">
              <Shield className="h-5 w-5 text-primary mx-auto mb-2" />
              <p>
                Por seguridad, el enlace expirará en 1 hora. Si no lo recibes, 
                puedes solicitar uno nuevo.
              </p>
            </div>

            <Link to="/login" className="btn-primary inline-flex items-center">
              <ArrowLeft className="h-5 w-5 mr-2" />
              Volver a Iniciar Sesión
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-dark-card rounded-lg shadow-xl p-8">
        <h2 className="text-2xl font-bold text-white text-center mb-4">
          Restablecer Contraseña
        </h2>
        
        <p className="text-gray-300 text-center mb-8">
          Introduce tu email y te enviaremos instrucciones para restablecer tu contraseña.
        </p>

        {error && (
          <div className="mb-6 p-3 bg-red-600/20 border border-red-600 rounded flex items-center text-red-100 text-sm">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-300">
              Correo Electrónico
            </label>
            <div className="mt-1 relative">
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value.trim());
                  setError('');
                }}
                className="input-field pl-10"
                required
                autoComplete="email"
                disabled={loading}
              />
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Enviando...
              </>
            ) : (
              'Enviar Instrucciones'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link 
            to="/login"
            className="text-gray-400 hover:text-white inline-flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Volver a Iniciar Sesión
          </Link>
        </div>

        <div className="mt-8 p-4 bg-dark-overlay rounded-lg">
          <h3 className="text-sm font-medium text-white mb-2 flex items-center">
            <Shield className="h-4 w-4 text-primary mr-2" />
            Información de Seguridad
          </h3>
          <ul className="text-sm text-gray-400 space-y-2">
            <li>• El enlace de recuperación expira en 1 hora</li>
            <li>• Máximo 3 intentos por hora</li>
            <li>• Verifica tu carpeta de spam</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;